import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { MoodFeed } from '@/types/content'

import { useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { ArrowRight, Clapperboard, Feather, Music2 } from 'lucide-react'
import { cms } from '@/lib/cms'
import { cn } from '@/lib/utils'
import { playableTracks } from '@/lib/tracks'
import { journalCategoryLabels, videoTypeLabels } from '@/lib/labels'
import { MoonPhase, phaseForIndex } from '@/components/media/moon-phase'
import { SongRow } from '@/components/music/song-row'
import { PlayAllButton } from '@/components/music/listen-button'
import { DemoBadge } from '@/components/site/demo-badge'

interface EmotionFinderProps {
    feeds: Array<MoodFeed>
    initialSlug?: string
    className?: string
}

/** Pick a feeling and discover the songs, writings and films filed under it. */
export const EmotionFinder: FC<EmotionFinderProps> = ({ feeds, initialSlug, className }): ReactNode => {
    const usable = feeds.filter(feed => feed.songs.length + feed.writings.length + feed.videos.length > 0)
    const list = usable.length > 0 ? usable : feeds
    const startIndex = Math.max(0, list.findIndex(feed => feed.mood.slug === initialSlug))
    const [selected, setSelected] = useState(startIndex)
    const tabs = useRef<Array<HTMLButtonElement | null>>([])
    const feed = list[selected] ?? list[0]
    const tracks = useMemo(() => (feed ? playableTracks(feed.songs) : []), [feed])

    if (!feed) return null

    const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
        const last = list.length - 1
        let target: number | null = null

        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') target = index === last ? 0 : index + 1
        if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') target = index === 0 ? last : index - 1
        if (event.key === 'Home') target = 0
        if (event.key === 'End') target = last

        if (target !== null) {
            event.preventDefault()
            setSelected(target)
            tabs.current[target]?.focus()
        }
    }

    const color = feed.mood.color ?? '#B69A70'

    return (
        <div className={className}>
            <div role='tablist' aria-label='Emotions' className='grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8'>
                {list.map((item, index) => {
                    const active = index === selected

                    return (
                        <button
                            key={item.mood.id}
                            ref={element => {
                                tabs.current[index] = element
                            }}
                            type='button'
                            role='tab'
                            id={`emotion-tab-${item.mood.slug}`}
                            aria-selected={active}
                            aria-controls='emotion-panel'
                            tabIndex={active ? 0 : -1}
                            onClick={() => setSelected(index)}
                            onKeyDown={event => onKeyDown(event, index)}
                            className={cn(
                                'group flex flex-col items-start gap-4 rounded-lg border p-4 text-left transition duration-500 ease-cinematic',
                                active ? 'border-gold/50 bg-ivory/[0.06]' : 'border-white/[0.06] hover:border-white/15 hover:bg-ivory/[0.03]'
                            )}
                        >
                            <MoonPhase phase={phaseForIndex(index, list.length)} size={30} color={item.mood.color ?? '#E9E4DA'} glow={active} />

                            <span className={cn('font-heading text-lg leading-tight transition-colors', active ? 'text-ivory' : 'text-ivory/70 group-hover:text-ivory')}>
                                <span {...cms('moods', item.mood.id, 'name')}>{item.mood.name}</span>
                            </span>

                            <span className='text-[0.65rem] uppercase tracking-[0.16em] text-ivory/35'>
                                {item.mood.song_count} {item.mood.song_count === 1 ? 'song' : 'songs'}
                            </span>
                        </button>
                    )
                })}
            </div>

            <div
                id='emotion-panel'
                role='tabpanel'
                aria-labelledby={`emotion-tab-${feed.mood.slug}`}
                key={feed.mood.id}
                className='relative mt-6 overflow-hidden rounded-2xl border border-white/[0.07] bg-charcoal/60 p-6 animate-in fade-in duration-500 sm:p-10'
            >
                <div className='pointer-events-none absolute -right-24 -top-24 size-80 rounded-full opacity-30 blur-3xl' style={{ backgroundColor: color }} aria-hidden='true' />

                <div className='relative grid gap-12 lg:grid-cols-12'>
                    <div className='lg:col-span-4'>
                        <p className='eyebrow' style={{ color }}>
                            Now feeling
                        </p>

                        <h3 className='mt-4 font-heading text-4xl font-light leading-tight sm:text-5xl'>
                            <span {...cms('moods', feed.mood.id, 'name')}>{feed.mood.name}</span>
                        </h3>

                        {feed.mood.prompt && (
                            <p className='mt-4 font-heading text-xl italic text-ivory/75'>
                                <span {...cms('moods', feed.mood.id, 'prompt')}>{feed.mood.prompt}</span>
                            </p>
                        )}

                        {feed.mood.description && (
                            <p className='mt-4 text-sm leading-relaxed text-muted-foreground'>
                                <span {...cms('moods', feed.mood.id, 'description')}>{feed.mood.description}</span>
                            </p>
                        )}

                        <div className='mt-8 flex flex-col items-start gap-4'>
                            {tracks.length > 0 && <PlayAllButton tracks={tracks} label='Play this feeling' />}

                            <Link href={`/emotions/${feed.mood.slug}`} className='group inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-gold transition hover:text-ivory'>
                                Everything in this feeling
                                <ArrowRight className='size-4 transition group-hover:translate-x-1' aria-hidden='true' />
                            </Link>
                        </div>
                    </div>

                    <div className='grid gap-10 lg:col-span-8 lg:grid-cols-2'>
                        <div className='lg:col-span-2'>
                            <p className='eyebrow mb-2 flex items-center gap-2 text-ivory/45'>
                                <Music2 className='size-3.5' aria-hidden='true' />
                                Songs
                            </p>

                            {feed.songs.length > 0 ? (
                                <ul>
                                    {feed.songs.map((song, index) => (
                                        <SongRow key={song.id} song={song} number={index + 1} context={tracks} />
                                    ))}
                                </ul>
                            ) : (
                                <p className='py-4 text-sm text-muted-foreground'>
                                    No songs are filed under this feeling yet.
                                </p>
                            )}
                        </div>

                        <div>
                            <p className='eyebrow mb-4 flex items-center gap-2 text-ivory/45'>
                                <Feather className='size-3.5' aria-hidden='true' />
                                Words
                            </p>

                            {feed.writings.length > 0 ? (
                                <ul className='space-y-5'>
                                    {feed.writings.map(entry => (
                                        <li key={entry.id}>
                                            <p className='text-[0.65rem] uppercase tracking-[0.16em] text-ivory/40'>
                                                {journalCategoryLabels[entry.category]}
                                            </p>

                                            <Link href={`/journal/${entry.slug}`} className='mt-1 block font-heading text-xl leading-snug text-ivory transition hover:text-gold'>
                                                <span {...cms('journal_entries', entry.id, 'title')}>{entry.title}</span>
                                            </Link>

                                            {entry.is_demo && <DemoBadge className='mt-2' />}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className='text-sm text-muted-foreground'>
                                    No writings here yet.
                                </p>
                            )}
                        </div>

                        <div>
                            <p className='eyebrow mb-4 flex items-center gap-2 text-ivory/45'>
                                <Clapperboard className='size-3.5' aria-hidden='true' />
                                Films
                            </p>

                            {feed.videos.length > 0 ? (
                                <ul className='space-y-5'>
                                    {feed.videos.map(video => (
                                        <li key={video.id}>
                                            <p className='text-[0.65rem] uppercase tracking-[0.16em] text-ivory/40'>
                                                {videoTypeLabels[video.video_type]}
                                            </p>

                                            <Link href={`/videos/${video.slug}`} className='mt-1 block font-heading text-xl leading-snug text-ivory transition hover:text-gold'>
                                                <span {...cms('videos', video.id, 'title')}>{video.title}</span>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className='text-sm text-muted-foreground'>
                                    No films here yet.
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
